import {
  LayoutDashboard,
  Image as ImageIcon,
  DollarSign,
  Sparkles,
  Receipt,
  HelpCircle,
  FileQuestion,
  RefreshCw,
  Users,
  Building2,
  AlertTriangle,
  ScrollText,
  History,
  GitMerge,
  type LucideIcon,
} from 'lucide-react';

// 관리자 섹션 키 (매니저 권한 managerPermissions의 키와 일치, 백엔드 roles.guard의 Section과도 동일)
export type Section =
  | 'dashboard'
  | 'permitImages'
  | 'subscriptions'
  | 'purchases'
  | 'faq'
  | 'drawingRequests'
  | 'drawingReports'
  | 'refunds'
  | 'users'
  | 'buildingSync';

// 접근 방식: permission=매니저 권한(view) 기반 / admin=관리자 이상 / superadmin=최고관리자만
export type AccessMode = 'permission' | 'admin' | 'superadmin';

export interface AdminMenu {
  href: string;
  label: string;
  icon: LucideIcon;
  section: Section;
  access: AccessMode;
  badge?: 'drawing' | 'refund' | 'report';
}

// 좌측 메뉴 · 접근 제어 · 페이지 게이팅의 단일 소스
export const ADMIN_MENUS: AdminMenu[] = [
  { href: '/dashboard', label: '대시보드', icon: LayoutDashboard, section: 'dashboard', access: 'permission' },
  { href: '/permit-images', label: '도면 관리', icon: ImageIcon, section: 'permitImages', access: 'permission' },
  { href: '/upload-history', label: '도면 업로드 이력', icon: History, section: 'permitImages', access: 'permission' },
  { href: '/pricing', label: '요금 관리', icon: DollarSign, section: 'subscriptions', access: 'permission' },
  { href: '/subscriptions', label: '구독 관리', icon: Sparkles, section: 'subscriptions', access: 'permission' },
  { href: '/purchases', label: '결제 내역', icon: Receipt, section: 'purchases', access: 'permission' },
  { href: '/samples', label: '샘플 관리', icon: Sparkles, section: 'permitImages', access: 'permission' },
  { href: '/faq', label: 'FAQ 관리', icon: HelpCircle, section: 'faq', access: 'permission' },
  { href: '/drawing-requests', label: '도면 요청', icon: FileQuestion, section: 'drawingRequests', access: 'permission', badge: 'drawing' },
  { href: '/drawing-reports', label: '도면 오류 제보', icon: AlertTriangle, section: 'drawingReports', access: 'permission', badge: 'report' },
  { href: '/refunds', label: '환불 요청', icon: RefreshCw, section: 'refunds', access: 'permission', badge: 'refund' },
  { href: '/users', label: '사용자 관리', icon: Users, section: 'users', access: 'admin' },
  { href: '/access-logs', label: '접속 로그', icon: ScrollText, section: 'users', access: 'superadmin' },
  { href: '/building-sync', label: '건축물 동기화', icon: Building2, section: 'buildingSync', access: 'superadmin' },
  { href: '/complex-dedup', label: '중복 단지 정리', icon: GitMerge, section: 'buildingSync', access: 'admin' },
];

// 경로 → 메뉴/섹션 (최장 href 일치). 리다이렉트/접근 판정용.
export function menuForPath(pathname: string): AdminMenu | null {
  const matched = ADMIN_MENUS.filter(
    (m) => pathname === m.href || pathname.startsWith(m.href + '/'),
  );
  if (matched.length === 0) return null;
  // 가장 구체적인(href가 긴) 메뉴 선택
  return matched.reduce((a, b) => (b.href.length > a.href.length ? b : a));
}

// 매니저에게 부여 가능한 섹션 목록(권한부여 UI용): permission 접근 + 도면관리(기본허용) 제외
export const MANAGER_ASSIGNABLE_SECTIONS: { key: Section; label: string }[] = ADMIN_MENUS.filter(
  (m) => m.access === 'permission' && m.section !== 'permitImages',
)
  // 섹션 중복(요금/구독은 둘 다 subscriptions) 제거
  .filter((m, i, arr) => arr.findIndex((x) => x.section === m.section) === i)
  .map((m) => ({ key: m.section, label: m.section === 'subscriptions' ? '구독/요금 관리' : m.label }));
